// ---------------------------------------------------------
// Datei: diagnosticExport.ts
// Zweck: Baut den textuellen "Diagnose exportieren"-Report (Queue-Zustand, letzte Fehler,
// Geräte-/Browser-Infos, Zeitstempel) und teilt ihn über die Web Share API bzw. Zwischenablage
// (ADO #1231, Kriterium 7).
// Kontext: Nimmt aus der Warteschlange bewusst NUR unbedenkliche Metadaten auf (Typ, Retries,
// Dokument-/Phasen-/Projekt-IDs, Dateianzahl) - NIE eingebettete Foto-/Datei-Base64-Daten
// (imageData/files[].dataUrl). Der Export ist für Support-Diagnose gedacht, kein Backup, und
// darf keine Rohbilder des Kunden enthalten.
// Stand: 2026-09-04
// ---------------------------------------------------------

import type { QueueItem } from "../interfaces/queue";
import { getDiagnosticLogEntries } from "./diagnosticLog";

const summarizeQueueItem = (item: QueueItem): string => {
  const data = (item.data || {}) as Record<string, unknown>;
  const parts = [
    `type=${item.type}`,
    `retries=${item.retries}`,
    `createdAt=${new Date(item.createdAt).toISOString()}`,
  ];
  if (typeof data.documentId === "string") parts.push(`documentId=${data.documentId}`);
  if (typeof data.phaseId === "string") parts.push(`phaseId=${data.phaseId}`);
  if (typeof data.projectId === "string") parts.push(`projectId=${data.projectId}`);
  if (typeof data.dealName === "string") parts.push(`dealName=${data.dealName}`);
  if (Array.isArray(data.files)) parts.push(`files=${data.files.length}`);
  return `- ${parts.join(" | ")}`;
};

export const buildDiagnosticReport = (queueItems: QueueItem[]): string => {
  const now = new Date();
  const logEntries = getDiagnosticLogEntries();

  const lines = [
    "=== 360Volt Diagnose-Export ===",
    `Zeitpunkt: ${now.toLocaleString("de-DE")} (${now.toISOString()})`,
    `App-Version: ${__APP_BUILD_ID__}`,
    `Online: ${navigator.onLine ? "ja" : "nein"}`,
    `User-Agent: ${navigator.userAgent}`,
    `Bildschirm: ${window.screen.width}x${window.screen.height} (Viewport ${window.innerWidth}x${window.innerHeight})`,
    "",
    `Warteschlange (${queueItems.length} Einträge):`,
    ...(queueItems.length > 0 ? queueItems.map(summarizeQueueItem) : ["- (leer)"]),
    "",
    `Letzte Fehler (${logEntries.length}):`,
    ...(logEntries.length > 0
      ? logEntries.map((entry) => `- [${entry.timestamp}] ${entry.message}`)
      : ["- (keine protokolliert)"]),
  ];

  return lines.join("\n");
};

/**
 * Teilt den Report über die Web Share API (funktioniert auf den Tablets, für die dieser Export
 * gedacht ist, direkt mit WhatsApp/Mail/...), fällt auf die Zwischenablage zurück, wenn
 * share() nicht verfügbar ist. Gibt zurück, WIE der Report den Nutzer erreicht hat, damit der
 * Aufrufer eine passende Toast-Meldung zeigen kann.
 */
export const shareDiagnosticReport = async (report: string): Promise<"shared" | "copied" | "cancelled"> => {
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ text: report, title: "360Volt Diagnose-Export" });
      return "shared";
    } catch (error) {
      // AbortError = Nutzer hat den Share-Dialog selbst abgebrochen, kein echter Fehler, für
      // den die Zwischenablage als Fallback greifen müsste.
      if (error instanceof Error && error.name === "AbortError") {
        return "cancelled";
      }
      console.error("[diagnosticExport] navigator.share fehlgeschlagen, falle auf Zwischenablage zurück:", error);
    }
  }

  if (typeof navigator.clipboard?.writeText === "function") {
    await navigator.clipboard.writeText(report);
    return "copied";
  }

  throw new Error("Weder Teilen noch Zwischenablage sind auf diesem Gerät verfügbar.");
};
